import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useEnrollmentProgress, useCompleteLesson } from '@/api/enrollments';
import { BackButton } from '@/components/shared/back-button';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { cn } from '@/lib/utils';
import { formatPercent } from '@/lib/formatters';
import { useCategoryLabel, useCategoryInfo } from '@/api/categories';
import { CategoryScope } from '@/components/shared/category-scope';
import { SectionCard } from './_components/section-card';
import { CourseCompleteBanner } from './course-complete-banner';
import type { SectionProgress } from '@/types';

const CATEGORY_COLORS: Record<string, string> = {
  induction: 'bg-blue-100 text-blue-700',
  periodic: 'bg-purple-100 text-purple-700',
  document_change: 'bg-amber-100 text-amber-700',
  general: 'bg-gray-100 text-gray-700',
  reinforcement: 'bg-teal-100 text-teal-700',
  technical: 'bg-cyan-100 text-cyan-700',
  soft_skills: 'bg-pink-100 text-pink-700',
  compliance: 'bg-indigo-100 text-indigo-700',
  mandatory: 'bg-red-100 text-red-700',
};

/** Find the first incomplete lesson across all sections (in order). */
function findNextLessonId(sections: SectionProgress[]): number | null {
  for (const section of [...sections].sort((a, b) => a.order - b.order)) {
    for (const lesson of [...section.lessons].sort((a, b) => a.order - b.order)) {
      if (!lesson.is_completed) return lesson.id;
    }
  }
  return null;
}

/** IDs of sections that are fully completed (should start collapsed). */
function completedSectionIds(
  sections: SectionProgress[],
  nextLessonId: number | null,
): Set<number> {
  const ids = new Set<number>();
  for (const s of sections) {
    const allDone = s.lessons.length > 0 && s.lessons.every((l) => l.is_completed);
    const containsNext = nextLessonId != null && s.lessons.some((l) => l.id === nextLessonId);
    if (allDone && !containsNext) ids.add(s.id);
  }
  return ids;
}

export function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const enrollmentId = Number(id) || 0;
  const categoryLabel = useCategoryLabel('course_category');
  const categoryInfo = useCategoryInfo('course_category');

  const { data: progress, isLoading, error } = useEnrollmentProgress(enrollmentId);
  const completeLesson = useCompleteLesson(enrollmentId);

  const nextLessonId = useMemo(
    () => (progress ? findNextLessonId(progress.sections) : null),
    [progress],
  );

  const [collapsedSections, setCollapsedSections] = useState<Set<number> | null>(null);

  // Lazy-initialize collapsed state once progress loads
  const effectiveCollapsed = useMemo(() => {
    if (collapsedSections) return collapsedSections;
    if (!progress) return new Set<number>();
    return completedSectionIds(progress.sections, nextLessonId);
  }, [collapsedSections, progress, nextLessonId]);

  function toggleSection(sectionId: number) {
    setCollapsedSections((prev) => {
      const base = prev ?? effectiveCollapsed;
      const next = new Set(base);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  if (isLoading) return <LoadingSpinner />;
  if (error || !progress) {
    return <ErrorMessage message="No se pudo cargar el progreso del curso." />;
  }

  const { enrollment, sections, overall_percent } = progress;
  const course = enrollment.course;
  const category = course.category;

  return (
    <div className="space-y-6">
      <BackButton fallback="/courses" label="Volver a Mis Cursos" />

      <CourseCompleteBanner percent={overall_percent} />

      {/* Course header */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {course.thumbnail_url && (
          <div className="h-40 w-full overflow-hidden bg-muted sm:h-52">
            <img
              src={course.thumbnail_url}
              alt={course.title}
              className="h-full w-full object-cover"
            />
          </div>
        )}
        <div className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-bold text-card-foreground sm:text-2xl">{course.title}</h1>
              {course.description && (
                <p className="mt-2 text-sm text-muted-foreground">{course.description}</p>
              )}
              <span
                className={cn(
                  'mt-3 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium',
                  CATEGORY_COLORS[category] ?? CATEGORY_COLORS.general,
                )}
              >
                {categoryLabel(category)}
              </span>
              <CategoryScope
                objective={categoryInfo(category)?.objective}
                includes={categoryInfo(category)?.includes}
              />
            </div>
            <div className="w-full sm:w-40">
              <p className="mb-1 text-right text-sm font-medium text-muted-foreground">
                Progreso general
              </p>
              <p className="text-right text-2xl font-bold text-card-foreground">
                {formatPercent(overall_percent)}
              </p>
              <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    overall_percent >= 100 ? 'bg-green-500' : 'bg-primary',
                  )}
                  style={{ width: `${Math.min(overall_percent, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Contenido del Curso</h2>

        {sections.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Este curso no tiene contenido disponible.
          </p>
        ) : (
          [...sections]
            .sort((a, b) => a.order - b.order)
            .map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                nextLessonId={nextLessonId}
                isCollapsed={effectiveCollapsed.has(section.id)}
                onToggle={() => toggleSection(section.id)}
                onComplete={(lessonId) => completeLesson.mutate(lessonId)}
                isCompleting={completeLesson.isPending}
                minVideoPercent={course.min_video_percent ?? undefined}
                hasInstructor={course.has_instructor}
                enrollmentId={enrollmentId}
              />
            ))
        )}
      </div>
    </div>
  );
}
